// Condition evaluation for showIf and scoring flags.
// Leaf: {field|context|score, op, value}; combinators: {all:[...]}, {any:[...]}, {not:{...}}.
// state = {answers, context, scores} where scores = {scaleId: number|null}.

function lookupField(path, answers = {}) {
  const [id, row] = path.split(".");
  const v = answers[id];
  if (row === undefined) return v;
  return v && typeof v === "object" ? v[row] : undefined;
}

function empty(v) {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function num(v) {
  if (v == null || v === "") return NaN;
  return typeof v === "number" ? v : Number(v);
}

export function evaluate(cond, state = {}) {
  if (!cond) return true;
  if (cond.all) return cond.all.every((c) => evaluate(c, state));
  if (cond.any) return cond.any.some((c) => evaluate(c, state));
  if (cond.not) return !evaluate(cond.not, state);

  let actual;
  if (cond.field) actual = lookupField(cond.field, state.answers);
  else if (cond.context) actual = (state.context || {})[cond.context];
  else if (cond.score) actual = (state.scores || {})[cond.score];
  else return true;

  const { op, value } = cond;
  switch (op) {
    case "answered":
      return !empty(actual);
    case "notAnswered":
      return empty(actual);
    case "eq":
      return Array.isArray(actual) ? false : actual === value || (actual != null && String(actual) === String(value));
    case "neq":
      return !(actual === value || (actual != null && String(actual) === String(value)));
    case "in":
      return Array.isArray(value) && value.map(String).includes(String(actual));
    case "notIn":
      return !(Array.isArray(value) && value.map(String).includes(String(actual)));
    case "includes": {
      const arr = Array.isArray(actual) ? actual : actual == null ? [] : [actual];
      const want = Array.isArray(value) ? value : [value];
      return want.some((w) => arr.map(String).includes(String(w)));
    }
    case "excludes": {
      const arr = Array.isArray(actual) ? actual : actual == null ? [] : [actual];
      const want = Array.isArray(value) ? value : [value];
      return !want.some((w) => arr.map(String).includes(String(w)));
    }
    case "gt":
      return num(actual) > num(value);
    case "gte":
      return num(actual) >= num(value);
    case "lt":
      return num(actual) < num(value);
    case "lte":
      return num(actual) <= num(value);
    default:
      return false;
  }
}

// Field ids a condition reads (for definition checks).
export function conditionRefs(cond, out = { fields: [], scores: [] }) {
  if (!cond) return out;
  if (cond.all) cond.all.forEach((c) => conditionRefs(c, out));
  if (cond.any) cond.any.forEach((c) => conditionRefs(c, out));
  if (cond.not) conditionRefs(cond.not, out);
  if (cond.field) out.fields.push(cond.field);
  if (cond.score) out.scores.push(cond.score);
  return out;
}
